import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@shared/components/ui/form";
import { ReloadIcon } from "@radix-ui/react-icons";
import { toast } from "sonner";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useResetPassword, useVerifyResetToken } from "../hooks/useForgotPassword";
import { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";

export default function ResetPasswordForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { mutate: resetPassword, isPending } = useResetPassword();
  const { mutate: verifyToken, isPending: isVerifying } = useVerifyResetToken();

  // Define the schema for form validation with translated messages
  const formSchema = z
    .object({
      password: z
        .string()
        .min(8, t("validation.passwordMinLength", { min: 8 }))
        .regex(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
          t("validation.passwordComplexity")
        ),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("validation.passwordsDoNotMatch"),
      path: ["confirmPassword"],
    });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  // Verify token on component mount
  useEffect(() => {
    if (!token) {
      toast.error(t("auth.invalidResetLink"));
      navigate("/forgot-password");
      return;
    }

    verifyToken(
      { token },
      {
        onError: (error) => {
          toast.error(
            error.response?.data?.message || t("auth.invalidResetLink")
          );
          navigate("/forgot-password");
        },
      }
    );
  }, [token, verifyToken, navigate, t]);

  function onSubmit(values) {
    if (!token) {
      toast.error(t("auth.invalidResetLink"));
      return;
    }

    const toastId = toast.loading(t("auth.resettingPassword"));

    resetPassword(
      { token, password: values.password },
      {
        onSuccess: (data) => {
          toast.success(data.message, {
            id: toastId,
          });
          form.reset();
          navigate("/login");
        },
        onError: (error) => {
          toast.error(
            error.response?.data?.message || t("auth.resetPasswordError"),
            {
              id: toastId,
            }
          );
        },
      }
    );
  }

  if (isVerifying) {
    return (
      <div className="text-center py-20">
        <ReloadIcon className="mx-auto h-8 w-8 animate-spin" />
        <p className="mt-4">{t("auth.verifyingResetLink")}</p>
      </div>
    );
  }

  return (
    <>
      <div className="text-center py-10">
        <h1 className="text-3xl font-bold">{t("auth.resetPassword")}</h1>
        <p className="text-muted-foreground mt-2">
          {t("auth.resetPasswordDescription")}
        </p>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4 flex flex-col justify-center"
        >
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("user.newPassword")}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder={t("auth.enter", {
                        entity: t("user.newPassword"),
                      })}
                      {...field}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("user.confirmPassword")}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder={t("auth.enter", {
                        entity: t("user.confirmPassword"),
                      })}
                      {...field}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" disabled={isPending} className="w-full">
            {isPending ? (
              <>
                <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                {t("main.loading")}
              </>
            ) : (
              t("auth.resetPassword")
            )}
          </Button>
        </form>
      </Form>

      <div className="mt-6 text-center text-sm">
        {t("auth.rememberPassword")}{" "}
        <Link to="/login" className="underline text-primary">
          {t("auth.backToLogin")}
        </Link>
      </div>
    </>
  );
}
